import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getAlbumById, getPlaylistTracks, getTrackById } from '../services/api';
import { getCreationTrack } from '../services/myCreationsApi';
import { Track } from '../types';
import { useCommonState } from './storeHooks/useCommonState';
import { useMusicState } from './storeHooks/useMusicState';

const createSingleTrackAlbum = (trackInfo: any, params: any) => {
  const trackWithUrls = {
    id: trackInfo.id,
    name: trackInfo.trackName,
    duration: 0,
    isLiked: false,
    urls: trackInfo.audioUrl?.urls || [],
    url:
      trackInfo.audioUrl?.urls && trackInfo.audioUrl.urls.length > 0
        ? undefined
        : trackInfo.audioUrl?.path +
          '/' +
          trackInfo.audioUrl?.fileName +
          '.mp3',
  };

  return {
    id: trackInfo?.album?.id || params.id,
    albumName: trackInfo.album?.albumName || trackInfo.trackName,
    albumArtUrl: trackInfo.album?.albumArtUrl,
    contributors: trackInfo.contributors,
    tracks: [trackWithUrls],
    category: trackInfo.album?.category || 'single',
    likes: 0,
    isLiked: false,
    upc: '',
    pk: '',
    description: '',
    genre: [],
    type: 'single',
  };
};

export const useAppRouting = () => {
  // State Management
  const { activePlaylist, setActivePlaylist } = useMusicState();
  const { setActivePopup, activePopup, isMinimized, setIsMinimized } =
    useCommonState();

  // Router Hooks
  const params = useParams();
  const location = useLocation();

  // Refs for tracking state - only keep what's actually needed
  const lastNonPlayerUrlRef = useRef<string>('/'); // Track last non-player page URL

  // Local State
  const [isLoadingAlbum, setIsLoadingAlbum] = useState<boolean>(false);
  const [isPlayerPage, setIsPlayerPage] = useState<boolean>(false);
  const [playlistError, setPlaylistError] = useState<string | null>(null);

  // Album loading function
  const loadAlbumData = useCallback(async () => {
    setIsLoadingAlbum(true);
    setPlaylistError(null); // Clear any previous error
    try {
      const urlParams = new URLSearchParams(location.search);
      const isSingleTrack = urlParams.get('ref') === 'favorite';
      const isOnPlayerRoute =
        location.pathname.startsWith('/player/') ||
        location.pathname.startsWith('/playlist/') ||
        location.pathname.startsWith('/creations/track/');

      // Check if this is a creation track URL
      const creationMatch = location.pathname.match(
        /^\/creations\/track\/([^\/]+)\/([^\/]+)$/,
      );
      if (creationMatch) {
        // Handle creation track playback
        const orgId = creationMatch[1];
        const trackId = creationMatch[2];

        try {
          const creationData = await getCreationTrack(orgId, trackId);

          // Check if track was not found (404)
          if ('error' in creationData && creationData.error === 'not_found') {
            setPlaylistError(
              "Looks like we couldn't find this track right now. It may have been removed or is no longer available.",
            );
            setActivePopup('musicplayer');
            return;
          }

          if (creationData && 'data' in creationData) {
            // Create a single-track album for this creation
            const trackData = creationData.data;
            const trackWithUrls = {
              id: creationData.id,
              name: trackData.title,
              duration: trackData.duration || 0,
              isLiked: false,
              url: trackData.url,
              urls: [],
            };

            // === DEMO HARDCODED DATA - REMOVE AFTER DEMO ===
            // Hardcoded Edo Segal contributor data for specific creation
            const DEMO_ORG_ID = '1665719d-80f3-4165-849b-3ff63399aeeb';
            const DEMO_CREATION_ID = '1d88879c-a8f6-484a-bd7a-5b2392356609';
            const isDemoCreation =
              orgId === DEMO_ORG_ID && trackId === DEMO_CREATION_ID;

            const hardcodedVideoMobilePreviewUrl = isDemoCreation
              ? 'https://napsteraimusic-dyewfxh4abb6ataq.z02.azurefd.net/aimusic/contributors/0618a946-4961-47b1-a977-8be9ff5eb6cb/0618a946-4961-47b1-a977-8be9ff5eb6cb_mobile_preview.jpg'
              : undefined;
            // === END DEMO HARDCODED DATA ===

            const singleTrackAlbum = {
              id: creationData.id,
              albumName: trackData.title,
              albumArtUrl:
                trackData.albumThumbnail ||
                trackData.previewImage ||
                creationData.contributor?.previewImage,
              // Use demo contributor for specific creation, otherwise use API data
              contributors: [
                {
                  id: creationData.contributor?.id || '',
                  givenName: creationData.contributor?.fullName || 'Unknown',
                  familyName: '',
                  slug: creationData.contributor?.slug || '',
                  thumbnailUrl: creationData.contributor?.previewImage || '',
                  videoDesktopPreviewUrl:
                    creationData.contributor?.previewVideoImage ||
                    creationData.contributor?.previewImage ||
                    '',
                  videoMobilePreviewUrl:
                    creationData.contributor?.previewMobileVideoImage ||
                    creationData.contributor?.previewImage ||
                    '',
                  videoDesktopUrl: creationData.contributor?.previewVideo || '',
                  videoMobileUrl:
                    creationData.contributor?.previewMobileVideo || '',
                },
              ],
              tracks: [trackWithUrls],
              category: 'my-creations',
              likes: 0,
              isLiked: false,
              upc: '',
              pk: '',
              description: trackData.description || '',
              genre: trackData.genres || [],
              type: 'favorite',
              orgId: orgId,
              creator: {
                id: creationData.creator?.id || '',
                fullName: creationData.creator?.fullName || 'Unknown',
                email: creationData.creator?.email,
              },
              ...(hardcodedVideoMobilePreviewUrl && {
                videoMobilePreviewUrl: hardcodedVideoMobilePreviewUrl,
              }),
            };
            console.log('singleTrackAlbum:', singleTrackAlbum);
            const playlist = {
              currentIndex: 0,
              album: singleTrackAlbum,
            };
            setActivePlaylist(playlist);
            setActivePopup('musicplayer');
          }
        } catch (error) {
          console.error('Error fetching creation track:', error);
        }
      } else if (location.pathname.match(/^\/playlist\/([^\/]+)\/([^\/]+)$/)) {
        // Handle playlist track playback
        const playlistMatch = location.pathname.match(
          /^\/playlist\/([^\/]+)\/([^\/]+)$/,
        );
        if (playlistMatch) {
          const playlistId = playlistMatch[1];
          const trackId = playlistMatch[2];

          try {
            const playlistData = await getPlaylistTracks(playlistId);
            console.log('Playlist API response:', playlistData);

            // Handle both possible response structures:
            // 1. playlistData.tracks as array
            // 2. playlistData.tracks.items as array (paginated response)
            const tracks = Array.isArray(playlistData?.tracks)
              ? playlistData.tracks
              : playlistData?.tracks?.items || [];

            if (playlistData && tracks.length > 0) {
              // Find the current track index
              let currentIndex = 0;
              let requestedTrack = null;
              if (trackId) {
                const trackIndex = tracks.findIndex(
                  (track: Track) => track.id === trackId,
                );
                if (trackIndex !== -1) {
                  currentIndex = trackIndex;
                  requestedTrack = tracks[trackIndex];
                }
              }

              // Check if the requested track is deleted/unavailable
              if (requestedTrack && requestedTrack.isAvailable === false) {
                setPlaylistError(
                  "Looks like we couldn't find this track right now. It may have been removed or is no longer available.",
                );
                setActivePopup('musicplayer');
                return;
              }

              // Create playlist album object
              const playlistAlbum = {
                id: playlistData.id,
                albumName: playlistData.name,
                albumArtUrl: playlistData.albumArtUrl || null,
                contributors: playlistData.contributors || [],
                tracks: tracks,
                category: playlistData.category || 'music',
                likes: 0,
                isLiked: false,
                upc: '',
                pk: '',
                description: playlistData.description || '',
                genre: [],
                type: 'playlist',
                privacy: playlistData.privacy,
              };

              const playlist = {
                currentIndex: currentIndex,
                album: playlistAlbum,
              };
              setActivePlaylist(playlist);
              setActivePopup('musicplayer');
            } else {
              // Playlist not found or has no tracks - set error state
              setPlaylistError(
                "Looks like we couldn't find this playlist right now. It may have been removed or is no longer available.",
              );
              setActivePopup('musicplayer');
            }
          } catch (error) {
            console.error('Error fetching playlist data:', error);
            setPlaylistError(
              "Looks like we couldn't find this playlist right now. It may have been removed or is no longer available.",
            );
            setActivePopup('musicplayer');
          }
        }
      } else {
        // Handle regular player/album URLs
        let trackId = params.trackId;
        let albumId = params.albumId;
        if (isOnPlayerRoute) {
          const playerMatch = location.pathname.match(
            /^\/player\/([^\/]+)\/([^\/]+)$/,
          );
          if (playerMatch) {
            albumId = playerMatch[1] || params.albumId;
            trackId = playerMatch[2] || params.trackId;
          }
        }

        if (isSingleTrack && trackId) {
          // Handle single track playback
          const trackInfo = await getTrackById(trackId);
          if (trackInfo) {
            const singleTrackAlbum = createSingleTrackAlbum(trackInfo, params);
            const playlist = {
              currentIndex: 0,
              album: singleTrackAlbum,
            };
            setActivePlaylist(playlist);
            setActivePopup('musicplayer');
          } else {
            setPlaylistError(
              "Looks like we couldn't find this track right now. It may have been removed or is no longer available.",
            );
          }
        } else if (albumId) {
          // Handle full album playback
          const albumData = await getAlbumById(albumId);
          if (albumData) {
            let currentIndex = 0;
            if (trackId) {
              const trackIndex = albumData.tracks.findIndex(
                (track: Track) => track.id === trackId,
              );
              if (trackIndex !== -1) {
                currentIndex = trackIndex;
              }
            }

            const playlist = {
              currentIndex: currentIndex,
              album: albumData,
            };
            setActivePlaylist(playlist);
            setActivePopup('musicplayer');
          } else {
            setPlaylistError(
              "Looks like we couldn't find this album right now. It may have been removed or is no longer available.",
            );
          }
        }
      }
    } catch (error) {
      console.error('Error fetching album/track:', error);
    } finally {
      setIsLoadingAlbum(false);
    }
  }, [
    params,
    location.search,
    location.pathname,
    setActivePlaylist,
    setActivePopup,
  ]);

  // Track last non-player URL for minimize navigation
  useEffect(() => {
    const isOnPlayerRoute =
      location.pathname.startsWith('/player/') ||
      location.pathname.startsWith('/playlist/') ||
      location.pathname.startsWith('/creations/track/');

    if (!isOnPlayerRoute) {
      // Update the last non-player URL
      lastNonPlayerUrlRef.current = location.pathname + location.search;
      console.log(
        '📍 Updated last non-player URL:',
        lastNonPlayerUrlRef.current,
      );
    }
  }, [location.pathname, location.search]);

  // Main routing effect - load album data when needed
  useEffect(() => {
    const isOnPlayerRoute =
      location.pathname.startsWith('/player/') ||
      location.pathname.startsWith('/playlist/') ||
      location.pathname.startsWith('/creations/track/');

    // Check if this is a playlist route and extract the playlist ID
    const playlistMatch = location.pathname.match(
      /^\/playlist\/([^\/]+)\/([^\/]+)$/,
    );
    const playlistIdFromUrl = playlistMatch ? playlistMatch[1] : null;

    // Check if we already have the correct playlist loaded
    const hasCorrectPlaylistLoaded =
      playlistIdFromUrl &&
      activePlaylist?.album?.id === playlistIdFromUrl &&
      activePlaylist?.album?.type === 'playlist';

    // For playlists, don't reload if we already have the correct one
    if (hasCorrectPlaylistLoaded) {
      return;
    }

    // Only run this if user is on direct player url and we need to fetch album data
    const needsAlbumFetch =
      params.albumId &&
      (!activePlaylist || activePlaylist.album.id !== params.albumId);

    if (needsAlbumFetch) {
      loadAlbumData();
    } else if (isOnPlayerRoute && !activePlaylist) {
      // Only load if we don't have any playlist loaded yet
      loadAlbumData();
    }
  }, [params.albumId, location.pathname]);

  // Handle location changes (including browser back/forward)
  useEffect(() => {
    const currentPath = location.pathname;
    const playerMatch =
      currentPath.match(/^\/player\/([^\/]+)\/([^\/]+)$/) ||
      currentPath.match(/^\/creations\/track\/([^\/]+)\/([^\/]+)$/i) ||
      currentPath.match(/^\/playlist\/([^\/]+)\/([^\/]+)$/i);
    const newIsPlayerPage = !!playerMatch;

    // Only log and update if the player page state actually changed
    if (newIsPlayerPage !== isPlayerPage) {
      console.log(
        'Location changed:',
        location.pathname,
        'isPlayerPage:',
        newIsPlayerPage,
        'activePopup:',
        activePopup,
        'isMinimized:',
        isMinimized,
      );
      setIsPlayerPage(newIsPlayerPage);

      if (
        newIsPlayerPage &&
        !isPlayerPage &&
        activePopup === 'musicplayer' &&
        isMinimized
      ) {
        // Navigating TO a music player URL from a different page - maximize the player
        console.log(
          'React Router - Navigating to player URL from different page - maximizing player',
        );
        setIsMinimized(false);
      } else if (
        isPlayerPage &&
        !newIsPlayerPage &&
        activePopup === 'musicplayer' &&
        !isMinimized
      ) {
        // Navigating AWAY from music player URL - minimize the player
        console.log(
          'React Router - Navigating away from player URL - minimizing player',
        );
        setIsMinimized(true);
      }
    } else if (
      newIsPlayerPage &&
      activePopup === 'musicplayer' &&
      isMinimized
    ) {
      // Already on player page but minimized - maximize it
      // This handles browser back button when already tracking as player page
      console.log('Already on player page but minimized - maximizing player');
      setIsMinimized(false);
    }
  }, [
    location.pathname,
    activePopup,
    isMinimized,
    setIsMinimized,
    isPlayerPage,
  ]);

  // Update URL when music player opens (but only when appropriate)
  useEffect(() => {
    if (activePlaylist && activePopup === 'musicplayer' && !isMinimized) {
      const trackId =
        activePlaylist.album['tracks'][activePlaylist.currentIndex]['id'];
      let newUrl;

      // Use different URL format based on album category
      if (activePlaylist.album.type === 'playlist') {
        newUrl = '/playlist/' + activePlaylist.album.id + '/' + trackId;
      } else if (activePlaylist.album.category === 'my-creations') {
        newUrl =
          '/creations/track/' + activePlaylist.album.orgId + '/' + trackId;
      } else {
        newUrl = '/player/' + activePlaylist.album.id + '/' + trackId;
      }

      const urlParams = new URLSearchParams(location.search);
      if (activePlaylist.album.type === 'favorite') {
        urlParams.set('ref', 'favorite');
      }
      const testMobile = urlParams.get('testMobile');
      const region = urlParams.get('region');
      if (testMobile) urlParams.set('testMobile', testMobile);
      if (region) urlParams.set('region', region);
      const queryString = urlParams.toString();
      if (queryString) {
        newUrl += '?' + queryString;
      }

      // Only update URL if we're not already on a specific page that should be preserved
      const currentPath = location.pathname;
      const isOnContributorPage = currentPath.match(
        /^\/contributor\/([^\/]+)$/,
      );

      // Don't change URL if on contributor page - let music play in background
      if (!isOnContributorPage) {
        // Use pushState to add player URL to history stack (allows proper back navigation)
        window.history.pushState(null, '', newUrl);
      }
    }
  }, [
    activePopup,
    isMinimized,
    location.pathname,
    location.search,
    activePlaylist,
  ]);

  // Reset player page state when music player closes
  useEffect(() => {
    if (activePopup !== 'musicplayer') {
      // Check if we're still on a player URL, if not, reset the player page state
      const currentPath = location.pathname;
      const playerMatch = currentPath.match(/^\/player\/([^\/]+)\/([^\/]+)$/);
      // Use a ref to get current isPlayerPage value without dependency
      if (!playerMatch) {
        console.log(
          'Music player closed and not on player URL - resetting player page state',
        );
        setIsPlayerPage(false);
      }
    }
  }, [activePopup, location.pathname]);

  // Use refs to access current state values in PopState handler
  const activePopupRef = useRef(activePopup);
  const isMinimizedRef = useRef(isMinimized);
  const setIsMinimizedRef = useRef(setIsMinimized);

  useEffect(() => {
    activePopupRef.current = activePopup;
  }, [activePopup]);

  useEffect(() => {
    isMinimizedRef.current = isMinimized;
  }, [isMinimized]);

  useEffect(() => {
    setIsMinimizedRef.current = setIsMinimized;
  }, [setIsMinimized]);

  // Handle browser back/forward button events
  useEffect(() => {
    const handlePopState = () => {
      console.log('PopState event detected:', window.location.pathname);

      const currentPath = window.location.pathname;
      const isPlayerUrl =
        currentPath.match(/^\/player\/([^\/]+)\/([^\/]+)$/) ||
        currentPath.match(/^\/playlist\/([^\/]+)\/([^\/]+)$/i) ||
        currentPath.match(/^\/creations\/track\/([^\/]+)\/([^\/]+)$/i);

      console.log(
        'PopState - isPlayerUrl:',
        !!isPlayerUrl,
        'activePopup:',
        activePopupRef.current,
        'isMinimized:',
        isMinimizedRef.current,
      );

      if (
        isPlayerUrl &&
        activePopupRef.current === 'musicplayer' &&
        isMinimizedRef.current
      ) {
        // Navigating TO a music player URL - maximize the player
        setIsMinimizedRef.current(false);
      } else if (
        !isPlayerUrl &&
        activePopupRef.current === 'musicplayer' &&
        !isMinimizedRef.current
      ) {
        // Navigating AWAY from music player URL - minimize the player
        setIsMinimizedRef.current(true);
      } else {
        setIsMinimizedRef.current(true);
      }
      console.log('PopState - End of handler');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Empty dependency array - setup only once

  // Function to get the last non-player URL for minimize navigation
  const getLastNonPlayerUrl = useCallback(() => {
    return lastNonPlayerUrlRef.current;
  }, []);

  return {
    isLoadingAlbum,
    isPlayerPage,
    loadAlbumData,
    getLastNonPlayerUrl,
    playlistError,
    clearPlaylistError: () => setPlaylistError(null),
  };
};
